import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { WorkspaceAccess } from '@pm/shared';
import { api } from '@/lib/api';
import { keys, type Home } from '@/lib/query';
import { useMentionable, type FolderItem, type WorkspaceListItem } from '../workspaces/api';

type Person = { id: string; name: string; avatarUrl: string | null };

export interface OrganizationItem {
  id: string;
  /** Null for the default organization until someone names it - see organizationName. */
  name: string | null;
  description: string | null;
  color: string | null;
  icon: string | null;
  isArchived: boolean;
  _count: { workspaces: number };
}

export interface OrganizationView extends OrganizationItem {
  /** Whoever manages organizations: workspaces, their places and owners. The API's answer, mirrored. */
  abilities: { manage: boolean };
}

/** One of an organization's workspaces. `access` is null on one the person cannot open. */
export interface OrganizationWorkspace extends Omit<WorkspaceListItem, 'access'> {
  access: WorkspaceAccess | null;
}

/** An organization's own folder, saying whether this person may rename, move or delete it. */
export interface OrganizationFolder extends FolderItem {
  canEdit: boolean;
}

/** The default organization has no name until someone gives it one. */
export const organizationName = (organization: { name: string | null } | null | undefined, t: (key: string) => string) =>
  organization?.name ?? t('organizations.defaultName');

/** Every organization for whoever manages them; for someone who only creates workspaces, the live ones. */
export const useOrganizations = (enabled = true) =>
  useQuery({
    queryKey: keys.organizations,
    queryFn: () => api.get<OrganizationItem[]>('/organizations'),
    enabled,
  });

export const useOrganization = (id: string) =>
  useQuery({
    queryKey: keys.organization(id),
    queryFn: () => api.get<OrganizationView>(`/organizations/${id}`),
    enabled: Boolean(id),
  });

export const useOrganizationWorkspaces = (id: string) =>
  useQuery({
    queryKey: keys.organizationWorkspaces(id),
    queryFn: () => api.get<OrganizationWorkspace[]>(`/organizations/${id}/workspaces`),
    enabled: Boolean(id),
  });

export const useOrganizationFolders = (id: string) =>
  useQuery({
    queryKey: keys.organizationFolders(id),
    queryFn: () => api.get<OrganizationFolder[]>(`/organizations/${id}/folders`),
    enabled: Boolean(id),
  });

/**
 * Who can open what lives in this home - a workspace's people, or an
 * organization's - for invites, handing over and the people a workflow picks.
 */
export function usePeopleIn(home: Home, enabled = true) {
  const inWorkspace = useMentionable(enabled && home.workspaceId ? home.workspaceId : '');
  const inOrganization = useQuery({
    queryKey: keys.organizationPeople(home.organizationId ?? ''),
    queryFn: () => api.get<Person[]>(`/organizations/${home.organizationId}/mentionable`),
    enabled: enabled && !home.workspaceId && Boolean(home.organizationId),
    staleTime: 5 * 60_000,
  });
  return home.workspaceId ? inWorkspace : inOrganization;
}

type OrganizationBody = { name?: string; description?: string | null; color?: string; isArchived?: boolean };

export function useOrganizationActions() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: keys.organizations });

  return {
    create: useMutation({
      mutationFn: (body: OrganizationBody & { name: string }) => api.post<OrganizationItem>('/organizations', body),
      onSuccess: refresh,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: OrganizationBody }) =>
        api.put<OrganizationItem>(`/organizations/${id}`, body),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete(`/organizations/${id}`),
      onSuccess: refresh,
    }),
  };
}

/**
 * A workspace to another organization, or another of its folders. What was
 * made inside it goes along, so both organizations' lists change.
 */
export function useMoveWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, organizationId, folderId }: {
      workspaceId: string; organizationId: string; folderId: string | null;
    }) => api.post(`/workspaces/${workspaceId}/move`, { organizationId, folderId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.organizations });
      void qc.invalidateQueries({ queryKey: keys.workspaces });
    },
  });
}
